import React, { useState, useEffect } from 'react';
import { useOrgAuth } from '../../../contexts/OrgAuthContext';
import {
  getProgrammes, createProgramme, updateProgramme, archiveProgramme,
  getProgrammeSessions, createSession,
  getSessionRegistrations, registerForSession, cancelSessionRegistration,
  generateSessionQRCodes, getSessionQRCodeUrl,
  getAssessmentQueue, claimAssessment, releaseAssessmentClaim, updateAssessmentStatus, reopenDeclinedApplication,
  getEnrolments, createEnrolment,
  addOrgApplicationMessage
} from '../../../api/programmesApi';
import { getMembers } from '../../../api/communityApi';
import { exportRowsToExcel } from '../../../utils/exportExcel';

// ── Eligibility matching helpers ──────────────────────────────────────────────
const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};
const parseIncome = (val) => {
  if (val == null || val === '') return null;
  const n = parseFloat(String(val).replace(/[$,\s]/g, ''));
  return isNaN(n) ? null : n;
};
const ageInGroup = (age, group) => {
  if (age === null) return false;
  if (group === 'Under 18') return age < 18;
  if (group === '18–35')    return age >= 18 && age <= 35;
  if (group === '36–55')    return age >= 36 && age <= 55;
  if (group === '55+')      return age >= 55;
  return false;
};
const matchesCriteria = (member, eligibility) => {
  if (!eligibility) return true;
  // Income check
  if (eligibility.maxIncome) {
    const income = parseIncome(member.monthlyIncome);
    if (income !== null && income > Number(eligibility.maxIncome)) return false;
  }
  // Age group check
  if (eligibility.ageGroups?.length) {
    const age = calcAge(member.dateOfBirth);
    if (age !== null && !eligibility.ageGroups.some(g => ageInGroup(age, g))) return false;
  }
  // Widow status: 'Widowed' matches members with widowStage set; others are advisory only
  if (eligibility.widowStatus?.length && eligibility.widowStatus.includes('Widowed')) {
    if (!member.widowStage) return false;
  }
  return true;
};

const TABS = [
  { key: 'library',      label: 'Organisation Programmes Library' },
  { key: 'referrals',    label: 'Applications Dashboard' },
];

const REFERRAL_BADGE = {
  pending_assessment:   { cls: 'bb-badge bb-badge--gray',  label: 'Referred' },
  assessment_scheduled: { cls: 'bb-badge', style: { background: '#DBEAFE', color: '#1E40AF' }, label: 'In Progress' },
  approved:             { cls: 'bb-badge bb-badge--green', label: 'Approved' },
  waitlisted:           { cls: 'bb-badge', style: { background: '#FEF9C3', color: '#92400E' }, label: 'Waitlisted' },
  enrolled:             { cls: 'bb-badge', style: { background: '#CCFBF1', color: '#0F766E' }, label: 'Enrolled' },
  on_hold:              { cls: 'bb-badge', style: { background: '#FFEDD5', color: '#9A3412' }, label: 'On Hold' },
  declined:             { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' }, label: 'Declined' },
  withdrawn:            { cls: 'bb-badge bb-badge--gray', label: 'Withdrawn' },
};

const ASSESSMENT_BADGE = {
  pending_assessment: { cls: 'bb-badge bb-badge--gray', label: 'Pending' },
  assessment_scheduled: { cls: 'bb-badge', style: { background: '#DBEAFE', color: '#1E40AF' }, label: 'Scheduled' },
  approved: { cls: 'bb-badge bb-badge--green', label: 'Approved' },
  waitlisted: { cls: 'bb-badge', style: { background: '#FEF9C3', color: '#92400E' }, label: 'Waitlisted' },
  enrolled: { cls: 'bb-badge', style: { background: '#CCFBF1', color: '#0F766E' }, label: 'Enrolled' },
  on_hold: { cls: 'bb-badge', style: { background: '#FFEDD5', color: '#9A3412' }, label: 'On Hold' },
  declined: { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' }, label: 'Declined' },
  withdrawn: { cls: 'bb-badge bb-badge--gray', label: 'Withdrawn' },
};

const WIDOW_STATUS_OPTIONS = ['Widowed', 'Divorced', 'Single Parent'];
const AGE_GROUP_OPTIONS    = ['Under 18', '18–35', '36–55', '55+'];

const EMPTY_PROGRAMME = {
  name: '', description: '',
  targetGroup: { type: 'widow', minAge: '', maxAge: '' },
  eligibility: { widowStatus: [], maxIncome: '', ageGroups: [], minDependentChildren: '' },
  capacity: '', format: 'group', scheduleDescription: ''
};

const EMPTY_SESSION = {
  programmeId: '', title: '', date: '', time: '', durationMinutes: '', location: '',
  locationMode: 'in-person', facilitatorName: '', notes: '',
  member_capacity: '', volunteer_capacity: '',
  volunteers_required: false, volunteers_needed: '',
  attendee_tracking_enabled: false,
};

const fmtDate = (ts) => ts ? new Date(ts?.toDate?.() || ts).toLocaleDateString('en-SG') : '—';

function SlaCell({ days }) {
  if (days >= 5) return <span style={{ color: '#DC2626', fontWeight: 600 }}>⚠️ {days}d</span>;
  return <span>{days}d</span>;
}

function daysSince(ts) {
  if (!ts) return 0;
  const d = new Date(ts?.toDate?.() || ts);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function OrganisationProgrammes({ orgId }) {
  const { currentUser } = useOrgAuth();
  const [activeTab, setActiveTab] = useState('library');

  // Programme Library
  const [programmes, setProgrammes] = useState([]);
  const [progLoading, setProgLoading] = useState(false);
  const [progError, setProgError] = useState('');
  const [showAddProg, setShowAddProg] = useState(false);
  const [progForm, setProgForm] = useState(EMPTY_PROGRAMME);
  const [progSubmitting, setProgSubmitting] = useState(false);
  const [editingProg, setEditingProg] = useState(null);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessLoading, setSessLoading] = useState(false);
  const [sessError, setSessError] = useState('');
  const [sessFilter, setSessFilter] = useState('');
  const [showAddSession, setShowAddSession] = useState(false);
  const [sessionForm, setSessionForm] = useState(EMPTY_SESSION);
  const [sessSubmitting, setSessSubmitting] = useState(false);

  // Session detail / registration management
  const [managingSession, setManagingSession] = useState(null); // session object
  const [sessionRegs, setSessionRegs] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regsError, setRegsError] = useState('');
  const [regForm, setRegForm] = useState({ userId: '', userName: '', userEmail: '', registrantType: 'member' });
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { userName, token }
  const [cancellingReg, setCancellingReg] = useState({});

  // Assessment Queue
  const [assessments, setAssessments] = useState([]);
  const [assLoading, setAssLoading] = useState(false);
  const [assError, setAssError] = useState('');
  const [declineModal, setDeclineModal] = useState(null); // { id, reason, altProgramme }
  const [reopenModal, setReopenModal] = useState(null); // { id, reason }
  const [assUpdating, setAssUpdating] = useState({});

  // Find Matches
  const [findMatchesProg, setFindMatchesProg] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null); // null = not run yet
  const [matchError, setMatchError] = useState('');

  const handleRunMatch = async (prog) => {
    setMatchLoading(true); setMatchResults(null); setMatchError('');
    try {
      const all = await getMembers(orgId);
      const active = all.filter(m => m.status !== 'archived' && m.status !== 'inactive');
      const matched = active.filter(m => matchesCriteria(m, prog.eligibility));
      setMatchResults(matched);
    } catch (e) {
      setMatchError(e.message || 'Failed to run match.');
    } finally {
      setMatchLoading(false);
    }
  };

  const exportMatchesToExcel = () => {
    if (!matchResults?.length) return;
    const rows = matchResults.map(m => {
      const age = calcAge(m.dateOfBirth);
      return {
        Name: m.displayName || m.name || '',
        Email: m.email || '',
        Age: age !== null ? age : '',
        'Monthly Income': m.monthlyIncome || '',
        'Widow Stage': m.widowStage || '',
        Status: m.status || 'active',
      };
    });
    exportRowsToExcel(rows, {
      sheetName: 'Matches',
      fileBase: `${findMatchesProg?.name || 'programme'}_matches`,
    });
  };

  const closeMatchModal = () => {
    setFindMatchesProg(null);
    setMatchResults(null);
    setMatchError('');
  };

  // Referral Dashboard
  const [referralFilter, setReferralFilter] = useState('all');
  const [referralProgFilter, setReferralProgFilter] = useState('all');
  const [refStatusModal, setRefStatusModal] = useState(null); // { referralId, newStatus }
  const [refStatusNote, setRefStatusNote] = useState('');
  const [refStatusUpdating, setRefStatusUpdating] = useState({});

  // Enrolments
  const [enrolments, setEnrolments] = useState([]);
  const [enrolLoading, setEnrolLoading] = useState(false);
  const [enrolError, setEnrolError] = useState('');
  const [enrolFilter, setEnrolFilter] = useState('');

  // Member Overview
  const [memberSearch, setMemberSearch] = useState('');

  // Referral view modal
  const [viewApp, setViewApp] = useState(null);
  const [appMessage, setAppMessage] = useState('');
  const [appMessageSending, setAppMessageSending] = useState(false);
  const [appsError, setAppsError] = useState('');

  useEffect(() => {
    if (activeTab === 'library') { loadProgrammes(); loadAssessments(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'referrals') { loadProgrammes(); loadAssessments(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'sessions') { loadProgrammes(); loadSessions(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'assessments') loadAssessments();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'enrolments') { loadProgrammes(); loadEnrolments(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'attendance') { loadProgrammes(); loadSessions(); loadEnrolments(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'members') { loadProgrammes(); loadAssessments(); loadEnrolments(); }
  }, [activeTab, orgId]);


  const loadProgrammes = async () => {
    setProgLoading(true); setProgError('');
    try { setProgrammes(await getProgrammes(orgId) || []); }
    catch (e) { setProgError(e.message || 'Failed to load programmes.'); }
    finally { setProgLoading(false); }
  };

  const loadSessions = async () => {
    setSessLoading(true); setSessError('');
    try { setSessions(await getProgrammeSessions(orgId) || []); }
    catch (e) { setSessError(e.message || 'Failed to load sessions.'); }
    finally { setSessLoading(false); }
  };

  const loadAssessments = async () => {
    setAssLoading(true); setAssError('');
    try { setAssessments(await getAssessmentQueue(orgId) || []); }
    catch (e) { setAssError(e.message || 'Failed to load assessment queue.'); }
    finally { setAssLoading(false); }
  };

  const loadEnrolments = async () => {
    setEnrolLoading(true); setEnrolError('');
    try { setEnrolments(await getEnrolments(orgId) || []); }
    catch (e) { setEnrolError(e.message || 'Failed to load enrolments.'); }
    finally { setEnrolLoading(false); }
  };

  const handleEditProg = (prog) => {
    setProgForm({
      name:                prog.name || '',
      description:         prog.description || '',
      targetGroup:         {
        type:   prog.targetGroup?.type   || 'widow',
        minAge: prog.targetGroup?.minAge || '',
        maxAge: prog.targetGroup?.maxAge || '',
      },
      eligibility: {
        widowStatus:          prog.eligibility?.widowStatus          || [],
        maxIncome:            prog.eligibility?.maxIncome            ?? '',
        ageGroups:            prog.eligibility?.ageGroups            || [],
        minDependentChildren: prog.eligibility?.minDependentChildren ?? '',
      },
      capacity:            prog.capacity || '',
      format:              prog.format   || 'group',
      scheduleDescription: prog.scheduleDescription || '',
    });
    setEditingProg(prog);
    setShowAddProg(true);
  };

  const toggleProgWidowStatus = (val) => setProgForm(p => {
    const list = p.eligibility.widowStatus.includes(val)
      ? p.eligibility.widowStatus.filter(v => v !== val)
      : [...p.eligibility.widowStatus, val];
    return { ...p, eligibility: { ...p.eligibility, widowStatus: list } };
  });

  const toggleProgAgeGroup = (val) => setProgForm(p => {
    const list = p.eligibility.ageGroups.includes(val)
      ? p.eligibility.ageGroups.filter(v => v !== val)
      : [...p.eligibility.ageGroups, val];
    return { ...p, eligibility: { ...p.eligibility, ageGroups: list } };
  });

  const handleAddProgramme = async (e) => {
    e.preventDefault(); setProgSubmitting(true); setProgError('');
    try {
      if (editingProg) {
        await updateProgramme(orgId, editingProg.id, { ...progForm, orgId });
      } else {
        await createProgramme(orgId, { ...progForm, orgId, createdBy: currentUser.uid });
      }
      setShowAddProg(false); setProgForm(EMPTY_PROGRAMME); setEditingProg(null);
      await loadProgrammes();
    } catch (e) { setProgError(e.message || 'Failed to save programme.'); }
    finally { setProgSubmitting(false); }
  };

  const handleArchiveProg = async (id) => {
    if (!window.confirm('Archive this programme?')) return;
    try { await archiveProgramme(orgId, id); await loadProgrammes(); }
    catch (e) { setProgError(e.message || 'Failed to archive.'); }
  };

  const handleAddSession = async (e) => {
    e.preventDefault(); setSessSubmitting(true); setSessError('');
    try {
      await createSession(orgId, { ...sessionForm, createdBy: currentUser.uid });
      setShowAddSession(false); setSessionForm(EMPTY_SESSION);
      await loadSessions();
    } catch (e) { setSessError(e.message || 'Failed to create session.'); }
    finally { setSessSubmitting(false); }
  };

  const handleClaimAss = async (id) => {
    setAssUpdating(p => ({ ...p, [id]: true }));
    try { await claimAssessment(orgId, id, currentUser.uid); await loadAssessments(); }
    catch (e) { setAssError(e.message || 'Failed to claim.'); }
    finally { setAssUpdating(p => ({ ...p, [id]: false })); }
  };

  const handleReleaseAss = async (id) => {
    setAssUpdating(p => ({ ...p, [id]: true }));
    try { await releaseAssessmentClaim(orgId, id); await loadAssessments(); }
    catch (e) { setAssError(e.message || 'Failed to release.'); }
    finally { setAssUpdating(p => ({ ...p, [id]: false })); }
  };

  const handleAssStatusChange = async (id, newStatus) => {
    if (newStatus === 'declined') { setDeclineModal({ id, reason: '', altProgramme: '' }); return; }
    setAssUpdating(p => ({ ...p, [id]: true }));
    try { await updateAssessmentStatus(orgId, id, newStatus, { updatedBy: currentUser.uid }); await loadAssessments(); }
    catch (e) { setAssError(e.message || 'Failed to update status.'); }
    finally { setAssUpdating(p => ({ ...p, [id]: false })); }
  };

  const handleDeclineSubmit = async () => {
    if (!declineModal.reason.trim()) { setAssError('Decline reason is required.'); return; }
    setAssUpdating(p => ({ ...p, [declineModal.id]: true }));
    try {
      await updateAssessmentStatus(orgId, declineModal.id, 'declined', {
        declineReason: declineModal.reason, altProgramme: declineModal.altProgramme, updatedBy: currentUser.uid
      });
      setDeclineModal(null); setAssError('');
      await loadAssessments();
    } catch (e) { setAssError(e.message || 'Failed to decline.'); }
    finally { setAssUpdating(p => ({ ...p, [declineModal.id]: false })); }
  };

  const handleReopenSubmit = async () => {
    if (!reopenModal.reason.trim()) { setAssError('Reason is required to re-open.'); return; }
    setAssUpdating(p => ({ ...p, [reopenModal.id]: true }));
    try {
      await reopenDeclinedApplication(orgId, reopenModal.id, { reason: reopenModal.reason, reopenedBy: currentUser.uid });
      setReopenModal(null); setAssError('');
      await loadAssessments();
    } catch (e) { setAssError(e.message || 'Failed to re-open.'); }
    finally { setAssUpdating(p => ({ ...p, [reopenModal.id]: false })); }
  };

  const handleRefClaim = async (referralId) => {
    setAssUpdating(p => ({ ...p, [referralId]: true }));
    try {
      await claimAssessment(orgId, referralId, currentUser.uid, adminName());
      await loadAssessments();
    } catch (e) { setAssError(e.message || 'Failed to claim referral.'); }
    finally { setAssUpdating(p => ({ ...p, [referralId]: false })); }
  };

  const handleRefRelease = async (id) => {
    try { await releaseAssessmentClaim(orgId, id); await loadAssessments(); }
    catch (e) { setAssError(e.message || 'Failed to release referral.'); }
  };

  const handleRefStatusChange = async () => {
    const { referralId, newStatus } = refStatusModal;
    setRefStatusUpdating(p => ({ ...p, [referralId]: true }));
    try {
      await updateAssessmentStatus(orgId, referralId, newStatus, { updatedBy: currentUser.uid, note: refStatusNote });
      setRefStatusModal(null); setRefStatusNote('');
      await loadAssessments();
    } catch (e) { setAssError(e.message || 'Failed to update referral status.'); }
    finally { setRefStatusUpdating(p => ({ ...p, [referralId]: false })); }
  };

  const adminName = () => currentUser.name || currentUser.displayName || currentUser.email || 'Admin';

  const handleViewApp = (app) => {
    setViewApp(app);
    setAppsError('');
    setAppMessage('');
  };

  const handleSendAppMessage = async () => {
    if (!appMessage.trim()) return;
    setAppMessageSending(true);
    setAppsError('');
    try {
      const name = adminName();
      await addOrgApplicationMessage(orgId, viewApp.id, currentUser.uid, name, 'admin', appMessage.trim());
      const msg = { senderId: currentUser.uid, senderName: name, senderRole: 'admin', text: appMessage.trim(), sentAt: new Date().toISOString() };
      setViewApp(prev => ({ ...prev, correspondence: [...(prev.correspondence || []), msg] }));
      setAppMessage('');
    } catch (e) {
      setAppsError(e.message || 'Failed to send message.');
    } finally {
      setAppMessageSending(false);
    }
  };

  const openManageSession = async (session) => {
    setManagingSession(session);
    setRegsError('');
    setRegForm({ userId: '', userName: '', userEmail: '', registrantType: 'member' });
    await loadSessionRegs(session.id);
  };

  const loadSessionRegs = async (sessionId) => {
    setRegsLoading(true); setRegsError('');
    try { setSessionRegs(await getSessionRegistrations(orgId, sessionId) || []); }
    catch (e) { setRegsError(e.message || 'Failed to load registrations.'); }
    finally { setRegsLoading(false); }
  };

  const handleAddRegistration = async (e) => {
    e.preventDefault();
    if (!regForm.userName.trim()) { setRegsError('Name is required.'); return; }
    setRegSubmitting(true); setRegsError('');
    try {
      await registerForSession(
        orgId, managingSession.id,
        regForm.userId || `manual-${Date.now()}`,
        regForm.userName, regForm.userEmail,
        regForm.registrantType, currentUser.uid
      );
      setRegForm({ userId: '', userName: '', userEmail: '', registrantType: 'member' });
      await loadSessionRegs(managingSession.id);
      await loadSessions();
    } catch (e) { setRegsError(e.message || 'Failed to register.'); }
    finally { setRegSubmitting(false); }
  };

  const handleCancelReg = async (reg) => {
    if (!window.confirm(`Remove ${reg.userName} from this session?`)) return;
    setCancellingReg(p => ({ ...p, [reg.id]: true }));
    try {
      await cancelSessionRegistration(orgId, managingSession.id, reg.id, 'Removed by admin', currentUser.uid);
      await loadSessionRegs(managingSession.id);
      await loadSessions();
    } catch (e) { setRegsError(e.message || 'Failed to remove registration.'); }
    finally { setCancellingReg(p => ({ ...p, [reg.id]: false })); }
  };

  const handleShowQR = async (reg) => {
    if (reg.qr_code_token) { setQrModal({ userName: reg.userName, token: reg.qr_code_token }); return; }
    try {
      const updated = await generateSessionQRCodes(orgId, managingSession.id);
      const found = updated.find(r => r.id === reg.id);
      if (found?.qr_code_token) setQrModal({ userName: found.userName, token: found.qr_code_token });
      await loadSessionRegs(managingSession.id);
    } catch (e) { setRegsError(e.message || 'Failed to generate QR code.'); }
  };

  const filteredSessions = sessions.filter(s => !sessFilter || s.programmeId === sessFilter);
  const filteredEnrolments = enrolments.filter(e => !enrolFilter || e.programmeId === enrolFilter);
  const progName = (id) => programmes.find(p => p.id === id)?.name || id;

  const memberMap = {};
  [...assessments, ...enrolments].forEach(item => {
    const mid = item.memberId;
    if (!mid) return;
    if (!memberMap[mid]) memberMap[mid] = { memberId: mid, memberName: item.memberName || mid, assessments: [], enrolments: [] };
    if (item.programmeId && item.status !== undefined) {
      if ('declineReason' in item || assessments.includes(item)) memberMap[mid].assessments.push(item);
      else memberMap[mid].enrolments.push(item);
    }
  });
  assessments.forEach(a => {
    const mid = a.memberId;
    if (!memberMap[mid]) memberMap[mid] = { memberId: mid, memberName: a.memberName || mid, assessments: [], enrolments: [] };
    if (!memberMap[mid].assessments.find(x => x.id === a.id)) memberMap[mid].assessments.push(a);
  });
  enrolments.forEach(e => {
    const mid = e.memberId;
    if (!memberMap[mid]) memberMap[mid] = { memberId: mid, memberName: e.memberName || mid, assessments: [], enrolments: [] };
    if (!memberMap[mid].enrolments.find(x => x.id === e.id)) memberMap[mid].enrolments.push(e);
  });
  const memberList = Object.values(memberMap).filter(m =>
    !memberSearch || m.memberName.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <div className="bb-editor">
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: activeTab === t.key ? '2px solid #6B46C1' : '2px solid transparent',
            color: activeTab === t.key ? '#6B46C1' : '#6B7280',
            fontWeight: activeTab === t.key ? 600 : 400, fontSize: 13
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── FIND MATCHES MODAL ── */}
      {findMatchesProg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 680, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <h3 style={{ margin: 0 }}>Find Matches — {findMatchesProg.name}</h3>
              <button onClick={closeMatchModal} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
              Matching your member database against the eligibility criteria below.
            </p>

            {/* Criteria summary */}
            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#374151' }}>Eligibility Criteria</div>
              {[
                findMatchesProg.eligibility?.widowStatus?.length ? `Widow Status: ${findMatchesProg.eligibility.widowStatus.join(', ')}` : null,
                findMatchesProg.eligibility?.maxIncome           ? `Max Monthly Income: $${findMatchesProg.eligibility.maxIncome}` : null,
                findMatchesProg.eligibility?.ageGroups?.length   ? `Age Groups: ${findMatchesProg.eligibility.ageGroups.join(', ')}` : null,
                findMatchesProg.eligibility?.minDependentChildren ? `Min Dependent Children: ${findMatchesProg.eligibility.minDependentChildren}` : null,
              ].filter(Boolean).map((line, i) => (
                <div key={i} style={{ color: '#6B7280', marginTop: 2 }}>• {line}</div>
              ))}
              {!findMatchesProg.eligibility?.widowStatus?.length &&
               !findMatchesProg.eligibility?.maxIncome &&
               !findMatchesProg.eligibility?.ageGroups?.length &&
               !findMatchesProg.eligibility?.minDependentChildren && (
                <div style={{ color: '#9CA3AF' }}>No criteria defined — all active members will match.</div>
              )}
            </div>

            {matchError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{matchError}</div>}

            {/* Run button */}
            {matchResults === null && (
              <div className="bb-editor-actions" style={{ marginBottom: 0 }}>
                <button className="bb-btn-primary" onClick={() => handleRunMatch(findMatchesProg)} disabled={matchLoading}>
                  {matchLoading ? 'Matching…' : 'Run Match'}
                </button>
                <button className="bb-btn-outline-sm" onClick={closeMatchModal}>Cancel</button>
              </div>
            )}

            {/* Results */}
            {matchResults !== null && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {matchResults.length === 0
                      ? 'No matching members found.'
                      : `${matchResults.length} matching member${matchResults.length !== 1 ? 's' : ''} found`}
                  </div>
                  <button className="bb-btn-outline-sm" onClick={() => handleRunMatch(findMatchesProg)} disabled={matchLoading} style={{ fontSize: 12 }}>
                    {matchLoading ? 'Re-running…' : 'Re-run'}
                  </button>
                </div>

                {matchResults.length > 0 && (
                  <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
                    <table className="bb-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Age</th>
                          <th>Monthly Income</th>
                          <th>Widow Stage</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchResults.map(m => {
                          const age = calcAge(m.dateOfBirth);
                          return (
                            <tr key={m.id}>
                              <td><strong>{m.displayName || m.name || '—'}</strong><br /><span style={{ fontSize: 11, color: '#9CA3AF' }}>{m.email}</span></td>
                              <td>{age !== null ? `${age} yrs` : '—'}</td>
                              <td>{m.monthlyIncome || '—'}</td>
                              <td style={{ textTransform: 'capitalize' }}>{m.widowStage || '—'}</td>
                              <td><span className={m.status === 'active' ? 'bb-badge bb-badge--green' : 'bb-badge bb-badge--gray'}>{m.status || 'active'}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <button className="bb-btn-secondary" onClick={exportMatchesToExcel} disabled={matchLoading || matchResults.length === 0}>
                    📥 Export to Excel
                  </button>
                  <button className="bb-btn-outline-sm" onClick={closeMatchModal}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── PROGRAMME LIBRARY ── */}
      {activeTab === 'library' && (
        <div>
          <div className="bb-editor-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0 }}>Organisation Programme Library</h2>
              <p className="bb-editor-desc">Manage all organisation-run programmes. All are assessment-gated by default.</p>
            </div>
            <button className="bb-btn-primary" onClick={() => { setEditingProg(null); setProgForm(EMPTY_PROGRAMME); setShowAddProg(v => !v); }}>
              {showAddProg && !editingProg ? 'Cancel' : '+ Add Organisation Programme'}
            </button>
          </div>
          {progError && <div className="bb-alert bb-alert--error">{progError}</div>}
          {showAddProg && (
            <form onSubmit={handleAddProgramme} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div className="bb-section-title" style={{ marginBottom: 16 }}>{editingProg ? 'Edit Organisation Programme' : 'New Organisation Programme'}</div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Programme Name <span className="bb-required">*</span></label>
                  <input className="bb-input" required value={progForm.name} onChange={e => setProgForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Grief Support Circle" />
                </div>
                <div className="bb-form-group">
                  <label>Format</label>
                  <select className="bb-select" value={progForm.format} onChange={e => setProgForm(p => ({ ...p, format: e.target.value }))}>
                    <option value="individual">Individual</option>
                    <option value="group">Group</option>
                    <option value="online">Online</option>
                  </select>
                </div>
              </div>
              <div className="bb-form-group">
                <label>Description</label>
                <textarea className="bb-textarea" rows={3} value={progForm.description} onChange={e => setProgForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the programme's purpose and approach." />
              </div>
              <div className="bb-form-section">
                <div className="bb-section-title">Eligibility Criteria</div>
                <div className="bb-form-group">
                  <label>Widow Status</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                    {WIDOW_STATUS_OPTIONS.map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={progForm.eligibility.widowStatus.includes(opt)} onChange={() => toggleProgWidowStatus(opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Max Monthly Household Income (SGD)</label>
                    <input className="bb-input" type="number" min={0} value={progForm.eligibility.maxIncome ?? ''} onChange={e => setProgForm(p => ({ ...p, eligibility: { ...p.eligibility, maxIncome: e.target.value } }))} placeholder="e.g. 2300 (leave blank for no limit)" />
                  </div>
                  <div className="bb-form-group">
                    <label>Min Dependent Children Count</label>
                    <input className="bb-input" type="number" min={0} value={progForm.eligibility.minDependentChildren ?? ''} onChange={e => setProgForm(p => ({ ...p, eligibility: { ...p.eligibility, minDependentChildren: e.target.value } }))} placeholder="e.g. 1 (leave blank for no minimum)" />
                  </div>
                </div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Capacity</label>
                    <input className="bb-input" type="number" min={1} value={progForm.capacity} onChange={e => setProgForm(p => ({ ...p, capacity: e.target.value }))} placeholder="e.g. 20" />
                  </div>
                </div>
                <div className="bb-form-group">
                  <label>Age Groups</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                    {AGE_GROUP_OPTIONS.map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={progForm.eligibility.ageGroups.includes(opt)} onChange={() => toggleProgAgeGroup(opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Target Group Type <span className="bb-hint">(used for member-side filtering)</span></label>
                  <select className="bb-select" value={progForm.targetGroup.type} onChange={e => setProgForm(p => ({ ...p, targetGroup: { ...p.targetGroup, type: e.target.value } }))}>
                    <option value="widow">Widow</option>
                    <option value="child">Child</option>
                  </select>
                </div>
                <div className="bb-form-group">
                  <label>Min Age</label>
                  <input className="bb-input" type="number" min={0} value={progForm.targetGroup.minAge} onChange={e => setProgForm(p => ({ ...p, targetGroup: { ...p.targetGroup, minAge: e.target.value } }))} placeholder="0" />
                </div>
                <div className="bb-form-group">
                  <label>Max Age</label>
                  <input className="bb-input" type="number" min={0} value={progForm.targetGroup.maxAge} onChange={e => setProgForm(p => ({ ...p, targetGroup: { ...p.targetGroup, maxAge: e.target.value } }))} placeholder="99" />
                </div>
              </div>
              <div className="bb-form-group">
                <label>Schedule Description</label>
                <input className="bb-input" value={progForm.scheduleDescription} onChange={e => setProgForm(p => ({ ...p, scheduleDescription: e.target.value }))} placeholder="e.g. Every Tuesday 7pm, 8 weeks" />
              </div>
              <div className="bb-editor-actions">
                <button className="bb-btn-primary" type="submit" disabled={progSubmitting}>{progSubmitting ? 'Saving…' : editingProg ? 'Save Changes' : 'Create Programme'}</button>
                <button className="bb-btn-outline-sm" type="button" onClick={() => { setShowAddProg(false); setProgForm(EMPTY_PROGRAMME); setEditingProg(null); }}>Cancel</button>
              </div>
            </form>
          )}
          {progLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={8}>Loading programmes…</td></tr></tbody></table>
          ) : programmes.length === 0 ? (
            <div className="bb-empty-state">No programmes yet. Click "+ Add Organisation Programme" to get started.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Name</th><th>Eligibility Criteria</th><th>Capacity</th><th>Format</th><th>Referrals</th><th>Approvals</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {programmes.map(p => {
                  const pReferrals = assessments.filter(a => a.programmeId === p.id).length;
                  const pApprovals = assessments.filter(a => a.programmeId === p.id && ['approved', 'enrolled'].includes(a.status)).length;
                  const eligSummary = [
                    p.eligibility?.widowStatus?.join(', '),
                    p.eligibility?.maxIncome ? `Income ≤ $${p.eligibility.maxIncome}` : null,
                    p.eligibility?.ageGroups?.join(', '),
                    p.eligibility?.minDependentChildren ? `Min ${p.eligibility.minDependentChildren} dependent child${p.eligibility.minDependentChildren === '1' || p.eligibility.minDependentChildren === 1 ? '' : 'ren'}` : null,
                  ].filter(Boolean).join(' · ') || '—';
                  return (
                    <tr key={p.id}>
                      <td><strong>{p.name}</strong></td>
                      <td style={{ fontSize: 12, color: '#6B7280', maxWidth: 220 }}>{eligSummary}</td>
                      <td>{p.capacity || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{p.format}</td>
                      <td style={{ textAlign: 'center' }}>{pReferrals}</td>
                      <td style={{ textAlign: 'center' }}>{pApprovals}</td>
                      <td>
                        <span className={p.status === 'archived' ? 'bb-badge bb-badge--gray' : 'bb-badge bb-badge--green'}>{p.status === 'archived' ? 'Archived' : 'Active'}</span>
                      </td>
                      <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {p.status !== 'archived' && <button className="bb-btn-sm" onClick={() => handleEditProg(p)}>Edit</button>}
                        {p.status !== 'archived' && <button className="bb-btn-danger-sm" onClick={() => handleArchiveProg(p.id)}>Archive</button>}
                        {p.status !== 'archived' && <button className="bb-btn-sm" onClick={() => setFindMatchesProg(p)}>Find Matches</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── REFERRAL DASHBOARD ── */}
      {activeTab === 'referrals' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Applications Dashboard</h2>
            <p className="bb-editor-desc">Track and action member applications to organisation programmes.</p>
          </div>

          {/* Status Update Modal */}
          {refStatusModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Update Referral Status</h3>
                <div style={{ fontSize: 14, marginBottom: 16 }}>
                  Changing status to{' '}
                  <span className={(REFERRAL_BADGE[refStatusModal.newStatus] || REFERRAL_BADGE.pending_assessment).cls}
                    style={(REFERRAL_BADGE[refStatusModal.newStatus] || REFERRAL_BADGE.pending_assessment).style}>
                    {(REFERRAL_BADGE[refStatusModal.newStatus] || REFERRAL_BADGE.pending_assessment).label}
                  </span>
                </div>
                <div className="bb-form-group">
                  <label>Note{(refStatusModal.newStatus === 'approved' || refStatusModal.newStatus === 'declined') && <span className="bb-required"> *</span>}</label>
                  <textarea
                    className="bb-textarea" rows={3} value={refStatusNote}
                    onChange={e => setRefStatusNote(e.target.value)}
                    placeholder={
                      refStatusModal.newStatus === 'approved' ? 'Describe the outcome and next steps for the member.' :
                      refStatusModal.newStatus === 'declined' ? 'Provide the reason for declining.' :
                      'Optional — describe current follow-up progress.'
                    }
                    autoFocus
                  />
                </div>
                <div className="bb-editor-actions">
                  <button
                    className="bb-btn-primary" onClick={handleRefStatusChange}
                    disabled={
                      refStatusUpdating[refStatusModal.referralId] ||
                      (['approved', 'declined'].includes(refStatusModal.newStatus) && !refStatusNote.trim())
                    }
                  >
                    {refStatusUpdating[refStatusModal.referralId] ? 'Saving…' : 'Confirm'}
                  </button>
                  <button className="bb-btn-outline-sm" onClick={() => { setRefStatusModal(null); setRefStatusNote(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, marginLeft: 24, alignItems: 'flex-end' }}>
            <div className="bb-form-group" style={{ margin: 0, minWidth: 160 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, display: 'block' }}>Status</label>
              <select className="bb-select" value={referralFilter} onChange={e => setReferralFilter(e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }}>
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="bb-form-group" style={{ margin: 0, minWidth: 220 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, display: 'block' }}>Programme</label>
              <select className="bb-select" value={referralProgFilter} onChange={e => setReferralProgFilter(e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }}>
                <option value="all">All Programmes</option>
                {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          {assError && <div className="bb-alert bb-alert--error">{assError}</div>}

          {/* View Referral Modal */}
          {viewApp && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 680, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>Referral Details</h3>
                  <button onClick={() => setViewApp(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
                </div>
                {appsError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{appsError}</div>}
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Member</div><div style={{ fontWeight: 600 }}>{viewApp.memberName || viewApp.memberId}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Programme</div><div style={{ fontWeight: 600 }}>{viewApp.programmeName || progName(viewApp.programmeId)}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Status</div><div>{(REFERRAL_BADGE[viewApp.status] || REFERRAL_BADGE.pending_assessment).label}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Submitted</div><div>{fmtDate(viewApp.createdAt)}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Last Updated</div><div>{fmtDate(viewApp.updatedAt)}</div></div>
                  {viewApp.claimedByName && <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Caseworker</div><div>{viewApp.claimedByName}</div></div>}
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Correspondence</div>
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, maxHeight: 260, overflowY: 'auto', padding: 12, marginBottom: 10, background: '#FAFAFA' }}>
                    {(viewApp.correspondence || []).length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '16px 0' }}>No messages yet.</div>
                    ) : (
                      [...(viewApp.correspondence || [])].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)).map((msg, i) => (
                        <div key={i} style={{ marginBottom: 12, display: 'flex', flexDirection: msg.senderRole === 'admin' ? 'row-reverse' : 'row', gap: 8 }}>
                          <div style={{ background: msg.senderRole === 'admin' ? '#6B46C1' : '#F3F4F6', color: msg.senderRole === 'admin' ? '#fff' : '#111827', padding: '8px 12px', borderRadius: 8, maxWidth: '75%', fontSize: 13 }}>
                            <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 3, opacity: 0.85 }}>{msg.senderName} · {msg.senderRole === 'admin' ? 'Admin' : 'Member'}</div>
                            <div>{msg.text}</div>
                            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>{msg.sentAt ? new Date(msg.sentAt).toLocaleString('en-SG', { dateStyle: 'short', timeStyle: 'short' }) : ''}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="bb-input" style={{ flex: 1 }} placeholder="Send a message to the member…" value={appMessage} onChange={e => setAppMessage(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendAppMessage(); } }} />
                    <button className="bb-btn-primary" onClick={handleSendAppMessage} disabled={!appMessage.trim() || appMessageSending}>{appMessageSending ? '…' : 'Send'}</button>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Documents</div>
                  {(viewApp.submittedDocuments || []).length === 0 ? (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No documents submitted by member.</div>
                  ) : (
                    (viewApp.submittedDocuments || []).map((docItem, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{docItem.name || 'Document'}</div>
                          {docItem.uploadedAt && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Uploaded {fmtDate(docItem.uploadedAt)}</div>}
                        </div>
                        {docItem.url && <a href={docItem.url} target="_blank" rel="noopener noreferrer" className="bb-btn-sm" style={{ textDecoration: 'none' }}>View</a>}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="bb-btn-outline-sm" onClick={() => setViewApp(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {(() => {
            const filteredReferrals = assessments.filter(r => {
              const statusMatch = referralFilter === 'all' ? true
                : referralFilter === 'open'     ? ['pending_assessment', 'assessment_scheduled', 'waitlisted', 'on_hold'].includes(r.status)
                : referralFilter === 'resolved' ? ['approved', 'enrolled', 'declined', 'withdrawn'].includes(r.status)
                : true;
              const progMatch = referralProgFilter === 'all' || r.programmeId === referralProgFilter;
              return statusMatch && progMatch;
            });

            if (assLoading) return <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={9}>Loading referrals…</td></tr></tbody></table>;
            if (filteredReferrals.length === 0) return <div className="bb-empty-state">No referrals found for this filter.</div>;

            return (
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Programme</th>
                    <th>Status</th>
                    <th>Days Open</th>
                    <th>Date Updated</th>
                    <th>Claimed By</th>
                    <th>Actions</th>
                    <th>Update Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferrals.map(r => {
                    const badge = REFERRAL_BADGE[r.status] || REFERRAL_BADGE.pending_assessment;
                    const isMine = r.claimedBy === currentUser.uid;
                    const isOpen = !['approved', 'enrolled', 'declined', 'withdrawn'].includes(r.status);
                    return (
                      <tr key={r.id}>
                        <td>{r.memberName || r.memberId}</td>
                        <td>{progName(r.programmeId)}</td>
                        <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                        <td><SlaCell days={r.daysOpen || 0} /></td>
                        <td>{fmtDate(r.updatedAt)}</td>
                        <td>{r.claimedByName || (r.claimedBy ? r.claimedBy : <span style={{ color: '#9CA3AF' }}>—</span>)}</td>
                        <td>
                          {isOpen && !r.claimedBy && (
                            <button className="bb-btn-sm" onClick={() => handleRefClaim(r.id)} disabled={!!assUpdating[r.id]}>Claim</button>
                          )}
                          {isOpen && isMine && (
                            <button className="bb-btn-outline-sm" onClick={() => handleRefRelease(r.id)}>Release</button>
                          )}
                        </td>
                        <td>
                          {isOpen && isMine && (
                            <select
                              className="bb-select" style={{ fontSize: 12, padding: '4px 8px' }}
                              value={r.status} disabled={refStatusUpdating[r.id]}
                              onChange={e => { if (e.target.value !== r.status) { setRefStatusModal({ referralId: r.id, newStatus: e.target.value }); setRefStatusNote(''); } }}
                            >
                              <option value="pending_assessment">Referred</option>
                              <option value="assessment_scheduled">In Progress</option>
                              <option value="approved">Approved</option>
                              <option value="waitlisted">Waitlisted</option>
                              <option value="on_hold">On Hold</option>
                              <option value="declined">Declined</option>
                            </select>
                          )}
                        </td>
                        <td>
                          <button className="bb-btn-sm" onClick={() => handleViewApp(r)}>View</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}

      {/* ── SESSION MANAGEMENT ── */}
      {activeTab === 'sessions' && (
        <div>
          <div className="bb-editor-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0 }}>Session Management</h2>
              <p className="bb-editor-desc">Schedule and manage programme sessions.</p>
            </div>
            <button className="bb-btn-primary" onClick={() => setShowAddSession(v => !v)}>{showAddSession ? 'Cancel' : '+ Add Session'}</button>
          </div>
          <div style={{ marginBottom: 16 }}>
            <select className="bb-select" style={{ width: 260 }} value={sessFilter} onChange={e => setSessFilter(e.target.value)}>
              <option value="">All Programmes</option>
              {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {sessError && <div className="bb-alert bb-alert--error">{sessError}</div>}
          {showAddSession && (
            <form onSubmit={handleAddSession} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div className="bb-section-title" style={{ marginBottom: 16 }}>New Session</div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Programme <span className="bb-required">*</span></label>
                  <select className="bb-select" required value={sessionForm.programmeId} onChange={e => setSessionForm(p => ({ ...p, programmeId: e.target.value }))}>
                    <option value="">Select programme…</option>
                    {programmes.filter(p => p.status !== 'archived').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="bb-form-group">
                  <label>Session Title</label>
                  <input className="bb-input" value={sessionForm.title} onChange={e => setSessionForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Week 3 — Coping Strategies" />
                </div>
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Date <span className="bb-required">*</span></label>
                  <input className="bb-input" type="date" required value={sessionForm.date} onChange={e => setSessionForm(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>Time <span className="bb-required">*</span></label>
                  <input className="bb-input" type="time" required value={sessionForm.time} onChange={e => setSessionForm(p => ({ ...p, time: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>Duration (mins)</label>
                  <input className="bb-input" type="number" min={15} value={sessionForm.durationMinutes} onChange={e => setSessionForm(p => ({ ...p, durationMinutes: e.target.value }))} placeholder="e.g. 90" />
                </div>
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Location</label>
                  <input className="bb-input" value={sessionForm.location} onChange={e => setSessionForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Meeting Room B" />
                </div>
                <div className="bb-form-group">
                  <label>Location Mode</label>
                  <select className="bb-select" value={sessionForm.locationMode} onChange={e => setSessionForm(p => ({ ...p, locationMode: e.target.value }))}>
                    <option value="in-person">In-person</option>
                    <option value="online">Online</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="bb-form-group">
                  <label>Facilitator Name</label>
                  <input className="bb-input" value={sessionForm.facilitatorName} onChange={e => setSessionForm(p => ({ ...p, facilitatorName: e.target.value }))} placeholder="e.g. Sarah Lim" />
                </div>
              </div>

              {/* Capacity */}
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#1D4ED8' }}>Registration Capacity</div>
                <div className="bb-form-row" style={{ marginBottom: 0 }}>
                  <div className="bb-form-group" style={{ marginBottom: 0 }}>
                    <label>Member Capacity <span className="bb-hint">(leave blank for unlimited)</span></label>
                    <input className="bb-input" type="number" min={1} style={{ maxWidth: 140 }} value={sessionForm.member_capacity} onChange={e => setSessionForm(p => ({ ...p, member_capacity: e.target.value }))} placeholder="e.g. 20" />
                  </div>
                  <div className="bb-form-group" style={{ marginBottom: 0 }}>
                    <label>Volunteer Capacity <span className="bb-hint">(leave blank for unlimited)</span></label>
                    <input className="bb-input" type="number" min={1} style={{ maxWidth: 140 }} value={sessionForm.volunteer_capacity} onChange={e => setSessionForm(p => ({ ...p, volunteer_capacity: e.target.value }))} placeholder="e.g. 5" />
                  </div>
                </div>
              </div>

              {/* Volunteer requirement */}
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: sessionForm.volunteers_required ? 12 : 0 }}>
                  <input type="checkbox" id="sessVolRequired" checked={sessionForm.volunteers_required}
                    onChange={e => setSessionForm(p => ({ ...p, volunteers_required: e.target.checked, volunteers_needed: e.target.checked ? p.volunteers_needed : '' }))} />
                  <label htmlFor="sessVolRequired" style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer', margin: 0 }}>This session requires volunteers</label>
                </div>
                {sessionForm.volunteers_required && (
                  <div className="bb-form-group" style={{ marginBottom: 0 }}>
                    <label>Number of Volunteers Needed <span className="bb-required">*</span></label>
                    <input className="bb-input" type="number" min={1} style={{ maxWidth: 120 }} value={sessionForm.volunteers_needed} required
                      onChange={e => setSessionForm(p => ({ ...p, volunteers_needed: e.target.value }))} placeholder="e.g. 2" />
                  </div>
                )}
              </div>

              {/* Attendee tracking */}
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="sessTracking" checked={sessionForm.attendee_tracking_enabled}
                    onChange={e => setSessionForm(p => ({ ...p, attendee_tracking_enabled: e.target.checked }))} />
                  <label htmlFor="sessTracking" style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer', margin: 0 }}>Enable attendee tracking (generates unique QR codes per registrant)</label>
                </div>
              </div>

              <div className="bb-form-group">
                <label>Notes</label>
                <textarea className="bb-textarea" rows={2} value={sessionForm.notes} onChange={e => setSessionForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any special instructions or notes for this session." />
              </div>
              <div className="bb-editor-actions">
                <button className="bb-btn-primary" type="submit" disabled={sessSubmitting}>{sessSubmitting ? 'Creating…' : 'Create Session'}</button>
                <button className="bb-btn-outline-sm" type="button" onClick={() => { setShowAddSession(false); setSessionForm(EMPTY_SESSION); }}>Cancel</button>
              </div>
            </form>
          )}
          {sessLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={8}>Loading sessions…</td></tr></tbody></table>
          ) : filteredSessions.length === 0 ? (
            <div className="bb-empty-state">No sessions found.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Date</th><th>Time</th><th>Programme</th><th>Facilitator</th><th>Members</th><th>Volunteers</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredSessions.map(s => {
                  const mCap = s.member_capacity != null ? parseInt(s.member_capacity, 10) : null;
                  const vCap = s.volunteer_capacity != null ? parseInt(s.volunteer_capacity, 10) : null;
                  const mReg = s.member_registered_count || 0;
                  const vReg = s.volunteer_registered_count || 0;
                  const mFull = mCap !== null && mReg >= mCap;
                  const vFull = vCap !== null && vReg >= vCap;
                  return (
                    <tr key={s.id}>
                      <td>{fmtDate(s.date)}</td>
                      <td>{s.time}</td>
                      <td>{s.title ? <><strong>{s.title}</strong><br /><span style={{ color: '#6B7280', fontSize: 12 }}>{progName(s.programmeId)}</span></> : progName(s.programmeId)}</td>
                      <td>{s.facilitatorName || '—'}</td>
                      <td>
                        <span style={{ fontWeight: 600, color: mFull ? '#DC2626' : '#374151' }}>
                          {mReg}{mCap !== null ? ` / ${mCap}` : ''}
                        </span>
                        {mFull && <span className="bb-badge" style={{ background: '#FEE2E2', color: '#991B1B', marginLeft: 4, fontSize: 10 }}>Full</span>}
                      </td>
                      <td>
                        {vCap !== null || s.volunteers_required ? (
                          <span style={{ fontWeight: 600, color: vFull ? '#DC2626' : '#374151' }}>
                            {vReg}{vCap !== null ? ` / ${vCap}` : ''}
                            {vFull && <span className="bb-badge" style={{ background: '#FEE2E2', color: '#991B1B', marginLeft: 4, fontSize: 10 }}>Full</span>}
                          </span>
                        ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                      </td>
                      <td>
                        <span className={s.status === 'completed' ? 'bb-badge bb-badge--gray' : 'bb-badge bb-badge--green'} style={{ textTransform: 'capitalize' }}>{s.status || 'Scheduled'}</span>
                        {s.attendee_tracking_enabled && <span title="Attendee tracking + QR codes enabled" style={{ marginLeft: 4, fontSize: 13 }}>📋</span>}
                      </td>
                      <td>
                        <button className="bb-btn-sm" onClick={() => openManageSession(s)}>Manage</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* ── Session Management Modal ── */}
          {managingSession && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 760, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{managingSession.title || progName(managingSession.programmeId)}</h3>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                      {fmtDate(managingSession.date)} · {managingSession.time}
                      {managingSession.location && ` · ${managingSession.location}`}
                      {managingSession.attendee_tracking_enabled && <span style={{ marginLeft: 8 }} title="QR codes enabled">📋 QR tracking enabled</span>}
                    </div>
                  </div>
                  <button onClick={() => { setManagingSession(null); setSessionRegs([]); setRegsError(''); }} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
                </div>

                {/* Capacity summary */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  {(() => {
                    const mCap = managingSession.member_capacity != null ? parseInt(managingSession.member_capacity, 10) : null;
                    const vCap = managingSession.volunteer_capacity != null ? parseInt(managingSession.volunteer_capacity, 10) : null;
                    const confirmed = sessionRegs.filter(r => r.status === 'CONFIRMED');
                    const mConf = confirmed.filter(r => r.registrantType === 'member').length;
                    const vConf = confirmed.filter(r => r.registrantType === 'volunteer').length;
                    const mWait = sessionRegs.filter(r => r.registrantType === 'member' && r.status === 'WAITLISTED').length;
                    const vWait = sessionRegs.filter(r => r.registrantType === 'volunteer' && r.status === 'WAITLISTED').length;
                    return (
                      <>
                        <div style={{ flex: 1, minWidth: 160, background: '#EFF6FF', borderRadius: 8, padding: '12px 16px' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#1D4ED8', marginBottom: 4 }}>MEMBERS</div>
                          <div style={{ fontSize: 22, fontWeight: 700 }}>{mConf}{mCap !== null ? ` / ${mCap}` : ''}</div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>confirmed{mWait > 0 ? ` · ${mWait} waitlisted` : ''}</div>
                          {mCap !== null && mConf >= mCap && <div style={{ fontSize: 11, color: '#DC2626', fontWeight: 600, marginTop: 4 }}>Registration full</div>}
                        </div>
                        <div style={{ flex: 1, minWidth: 160, background: '#FFFBEB', borderRadius: 8, padding: '12px 16px' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E', marginBottom: 4 }}>VOLUNTEERS</div>
                          <div style={{ fontSize: 22, fontWeight: 700 }}>{vConf}{vCap !== null ? ` / ${vCap}` : '—'}</div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>confirmed{vWait > 0 ? ` · ${vWait} waitlisted` : ''}</div>
                          {vCap !== null && vConf >= vCap && <div style={{ fontSize: 11, color: '#DC2626', fontWeight: 600, marginTop: 4 }}>Registration full</div>}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {regsError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{regsError}</div>}

                {/* Add registration form */}
                <form onSubmit={handleAddRegistration} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Register a Member or Volunteer</div>
                  <div className="bb-form-row">
                    <div className="bb-form-group" style={{ marginBottom: 0 }}>
                      <label>Type</label>
                      <select className="bb-select" value={regForm.registrantType} onChange={e => setRegForm(p => ({ ...p, registrantType: e.target.value }))}>
                        <option value="member">Member</option>
                        <option value="volunteer">Volunteer</option>
                      </select>
                    </div>
                    <div className="bb-form-group" style={{ marginBottom: 0 }}>
                      <label>Name <span className="bb-required">*</span></label>
                      <input className="bb-input" value={regForm.userName} onChange={e => setRegForm(p => ({ ...p, userName: e.target.value }))} placeholder="Full name" />
                    </div>
                    <div className="bb-form-group" style={{ marginBottom: 0 }}>
                      <label>Email</label>
                      <input className="bb-input" type="email" value={regForm.userEmail} onChange={e => setRegForm(p => ({ ...p, userEmail: e.target.value }))} placeholder="email@example.com" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button className="bb-btn-primary" type="submit" disabled={regSubmitting}>{regSubmitting ? 'Adding…' : 'Add'}</button>
                    </div>
                  </div>
                </form>

                {/* Registrations list */}
                {regsLoading ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Loading registrations…</div>
                ) : (
                  <>
                    {/* Confirmed */}
                    {['member', 'volunteer'].map(rType => {
                      const confirmed = sessionRegs.filter(r => r.registrantType === rType && r.status === 'CONFIRMED');
                      const waitlisted = sessionRegs.filter(r => r.registrantType === rType && r.status === 'WAITLISTED');
                      if (confirmed.length === 0 && waitlisted.length === 0) return null;
                      return (
                        <div key={rType} style={{ marginBottom: 20 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: rType === 'volunteer' ? '#92400E' : '#1D4ED8', marginBottom: 8, textTransform: 'capitalize' }}>
                            {rType === 'volunteer' ? 'Volunteers' : 'Members'} ({confirmed.length} confirmed{waitlisted.length > 0 ? `, ${waitlisted.length} waitlisted` : ''})
                          </div>
                          <table className="bb-table" style={{ marginBottom: 8 }}>
                            <thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Registered</th>{managingSession.attendee_tracking_enabled && <th>QR Code</th>}<th>Actions</th></tr></thead>
                            <tbody>
                              {[...confirmed, ...waitlisted].map(reg => (
                                <tr key={reg.id}>
                                  <td>{reg.userName}</td>
                                  <td style={{ color: '#6B7280', fontSize: 12 }}>{reg.userEmail || '—'}</td>
                                  <td>
                                    {reg.status === 'CONFIRMED'
                                      ? <span className="bb-badge bb-badge--green">Confirmed</span>
                                      : <span className="bb-badge" style={{ background: '#FEF3C7', color: '#92400E' }}>Waitlist #{reg.waitlist_position}</span>}
                                  </td>
                                  <td style={{ fontSize: 12, color: '#6B7280' }}>{fmtDate(reg.registeredAt)}</td>
                                  {managingSession.attendee_tracking_enabled && (
                                    <td>
                                      {reg.status === 'CONFIRMED'
                                        ? <button className="bb-btn-sm" style={{ fontSize: 11 }} onClick={() => handleShowQR(reg)}>View QR</button>
                                        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                                    </td>
                                  )}
                                  <td>
                                    <button className="bb-btn-danger-sm" disabled={cancellingReg[reg.id]} onClick={() => handleCancelReg(reg)}>
                                      {cancellingReg[reg.id] ? '…' : 'Remove'}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                    {sessionRegs.filter(r => r.status !== 'CANCELLED').length === 0 && (
                      <div className="bb-empty-state">No registrations yet.</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── QR Code Modal ── */}
          {qrModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 340, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>QR Code</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>{qrModal.userName}</p>
                <img
                  src={getSessionQRCodeUrl(qrModal.token, 220)}
                  alt={`QR code for ${qrModal.userName}`}
                  style={{ width: 220, height: 220, borderRadius: 8, border: '1px solid #E5E7EB' }}
                />
                <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12, wordBreak: 'break-all' }}>{qrModal.token}</p>
                <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <a
                    href={getSessionQRCodeUrl(qrModal.token, 400)}
                    download={`qr-${qrModal.userName.replace(/\s+/g, '-')}.png`}
                    className="bb-btn-primary"
                    style={{ fontSize: 13, padding: '8px 16px', textDecoration: 'none' }}
                  >Download</a>
                  <button className="bb-btn-outline-sm" onClick={() => setQrModal(null)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ASSESSMENT QUEUE ── */}
      {activeTab === 'assessments' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Assessment Queue</h2>
            <p className="bb-editor-desc">SLA view for programme applications. Flag cases open 5+ days.</p>
          </div>
          {assError && <div className="bb-alert bb-alert--error">{assError}</div>}

          {/* Decline Modal */}
          {declineModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Decline Application</h3>
                <div className="bb-form-group">
                  <label>Decline Reason <span className="bb-required">*</span></label>
                  <textarea className="bb-textarea" rows={3} value={declineModal.reason} onChange={e => setDeclineModal(p => ({ ...p, reason: e.target.value }))} placeholder="Explain why this application is being declined." />
                </div>
                <div className="bb-form-group">
                  <label>Suggested Alternative Programme <span className="bb-hint">(optional)</span></label>
                  <input className="bb-input" value={declineModal.altProgramme} onChange={e => setDeclineModal(p => ({ ...p, altProgramme: e.target.value }))} placeholder="e.g. Refer to ComCare short-term assistance" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={handleDeclineSubmit}>Confirm Decline</button>
                  <button className="bb-btn-outline-sm" onClick={() => setDeclineModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Reopen Modal */}
          {reopenModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Re-open Declined Application</h3>
                <div className="bb-form-group">
                  <label>Reason for Re-opening <span className="bb-required">*</span></label>
                  <textarea className="bb-textarea" rows={3} value={reopenModal.reason} onChange={e => setReopenModal(p => ({ ...p, reason: e.target.value }))} placeholder="Why is this application being re-opened?" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-primary" onClick={handleReopenSubmit}>Re-open Application</button>
                  <button className="bb-btn-outline-sm" onClick={() => setReopenModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {assLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={7}>Loading assessment queue…</td></tr></tbody></table>
          ) : assessments.length === 0 ? (
            <div className="bb-empty-state">No assessment applications in queue.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Member</th><th>Programme</th><th>Days Open</th><th>Status</th><th>Claimed By</th><th>Actions</th><th>Update Status</th></tr></thead>
              <tbody>
                {assessments.map(a => {
                  const days = daysSince(a.createdAt);
                  const badge = ASSESSMENT_BADGE[a.status] || ASSESSMENT_BADGE.pending_assessment;
                  const isMine = a.claimedBy === currentUser.uid;
                  const isEditable = !['enrolled', 'withdrawn'].includes(a.status);
                  return (
                    <tr key={a.id}>
                      <td>{a.memberName || a.memberId}</td>
                      <td>{progName(a.programmeId)}</td>
                      <td><SlaCell days={days} /></td>
                      <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                      <td>{a.claimedByName || (a.claimedBy ? a.claimedBy : <span style={{ color: '#9CA3AF' }}>—</span>)}</td>
                      <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {isEditable && !a.claimedBy && <button className="bb-btn-sm" disabled={assUpdating[a.id]} onClick={() => handleClaimAss(a.id)}>Claim</button>}
                        {isEditable && isMine && <button className="bb-btn-outline-sm" disabled={assUpdating[a.id]} onClick={() => handleReleaseAss(a.id)}>Release</button>}
                        {a.status === 'declined' && <button className="bb-btn-outline-sm" onClick={() => setReopenModal({ id: a.id, reason: '' })}>Re-open</button>}
                      </td>
                      <td>
                        {isEditable && isMine && (
                          <select className="bb-select" style={{ fontSize: 12, padding: '4px 8px' }} value={a.status} disabled={assUpdating[a.id]}
                            onChange={e => handleAssStatusChange(a.id, e.target.value)}>
                            <option value="pending_assessment">Pending Assessment</option>
                            <option value="assessment_scheduled">Assessment Scheduled</option>
                            <option value="approved">Approved</option>
                            <option value="waitlisted">Waitlisted</option>
                            <option value="on_hold">On Hold</option>
                            <option value="declined">Declined</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ENROLMENTS ── */}
      {activeTab === 'enrolments' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Enrolments</h2>
            <p className="bb-editor-desc">View and manage all programme enrolments.</p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <select className="bb-select" style={{ width: 260 }} value={enrolFilter} onChange={e => setEnrolFilter(e.target.value)}>
              <option value="">All Programmes</option>
              {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {enrolError && <div className="bb-alert bb-alert--error">{enrolError}</div>}
          {enrolLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading enrolments…</td></tr></tbody></table>
          ) : filteredEnrolments.length === 0 ? (
            <div className="bb-empty-state">No enrolments found.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Member</th><th>Programme</th><th>Status</th><th>Enrol Date</th><th>Progress</th></tr></thead>
              <tbody>
                {filteredEnrolments.map(e => (
                  <tr key={e.id}>
                    <td>{e.memberName || e.memberId}</td>
                    <td>{progName(e.programmeId)}</td>
                    <td><span className={e.status === 'active' ? 'bb-badge bb-badge--green' : 'bb-badge bb-badge--gray'}>{e.status || 'Active'}</span></td>
                    <td>{fmtDate(e.enrolDate || e.createdAt)}</td>
                    <td>{e.currentWeek && e.totalWeeks ? `Week ${e.currentWeek} of ${e.totalWeeks}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ATTENDANCE & REPORTS ── */}
      {activeTab === 'attendance' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Attendance & Reports</h2>
            <p className="bb-editor-desc">Per-programme attendance rates and session summaries.</p>
          </div>
          {programmes.filter(p => p.status !== 'archived').length === 0 ? (
            <div className="bb-empty-state">No active programmes to report on.</div>
          ) : (
            <>
              <div className="bb-stats-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
                {programmes.filter(p => p.status !== 'archived').map(p => {
                  const pSessions = sessions.filter(s => s.programmeId === p.id);
                  const pEnrolments = enrolments.filter(e => e.programmeId === p.id).length;
                  const attendanceRate = pSessions.length > 0
                    ? Math.round(pSessions.reduce((sum, s) => sum + (s.attendanceRate || 0), 0) / pSessions.length)
                    : 0;
                  return (
                    <div key={p.id} className="bb-stat-card">
                      <div className="bb-stat-value">{attendanceRate}%</div>
                      <div className="bb-stat-label">{p.name}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{pSessions.length} sessions · {pEnrolments} enrolled</div>
                    </div>
                  );
                })}
              </div>
              <div className="bb-section-title" style={{ marginBottom: 12 }}>Session Detail</div>
              <table className="bb-table">
                <thead><tr><th>Session Date</th><th>Programme</th><th>Facilitator</th><th>Attendance Rate</th><th>Status</th></tr></thead>
                <tbody>
                  {sessions.length === 0
                    ? <tr className="bb-loading-row"><td colSpan={5}>No sessions recorded.</td></tr>
                    : sessions.map(s => (
                      <tr key={s.id}>
                        <td>{fmtDate(s.date)}</td>
                        <td>{progName(s.programmeId)}</td>
                        <td>{s.facilitatorName || '—'}</td>
                        <td>{s.attendanceRate !== undefined ? `${s.attendanceRate}%` : '—'}</td>
                        <td><span className={s.status === 'completed' ? 'bb-badge bb-badge--gray' : 'bb-badge bb-badge--green'}>{s.status || 'Scheduled'}</span></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ── MEMBER OVERVIEW ── */}
      {activeTab === 'members' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Member Overview</h2>
            <p className="bb-editor-desc">View programme history for each member.</p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <input className="bb-input" style={{ maxWidth: 320 }} placeholder="Search by member name…" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} />
          </div>
          {memberList.length === 0 ? (
            <div className="bb-empty-state">No members found.</div>
          ) : (
            memberList.map(m => (
              <div key={m.memberId} className="bb-card" style={{ marginBottom: 16 }}>
                <div className="bb-card-header"><strong>{m.memberName}</strong></div>
                <div style={{ padding: '12px 16px' }}>
                  {m.assessments.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Assessment Requests</div>
                      {m.assessments.map(a => {
                        const badge = ASSESSMENT_BADGE[a.status] || ASSESSMENT_BADGE.pending_assessment;
                        return (
                          <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 2 }}>
                            <span>{progName(a.programmeId)}</span>
                            <span className={badge.cls} style={badge.style}>{badge.label}</span>
                            <span style={{ color: '#9CA3AF' }}>{fmtDate(a.createdAt)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {m.enrolments.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Enrolments</div>
                      {m.enrolments.map(e => (
                        <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 2 }}>
                          <span>{progName(e.programmeId)}</span>
                          <span className={e.status === 'active' ? 'bb-badge bb-badge--green' : 'bb-badge bb-badge--gray'}>{e.status || 'Active'}</span>
                          <span style={{ color: '#9CA3AF' }}>{fmtDate(e.enrolDate || e.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.assessments.length === 0 && m.enrolments.length === 0 && (
                    <span style={{ color: '#9CA3AF', fontSize: 13 }}>No programme history.</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
